import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Modal, Skeleton } from 'antd'
import { classNames } from '../../../../lib/classNames/classNames'
import { DefaultJsonForm } from '../../../v2/default-json-form'
import { useAutoCloseModal } from '../../../../hooks/useAutoCloseModal'
import { convertData2JsonForm } from '../../../../shared/lib/convertData2JsonForm'
import { schema } from '../../model/schema/modal-schema'
import { isFormDataRequired } from '../../../../../lib/utils/collections'
import { setShippingDate } from '../../model/api/setShippingDate'
import { useSelector } from 'react-redux'
import { getDeliveriesForm } from '../../../../store/deliveries/action-creators'
import { getDeliveriesEnumsData } from '../../../../pages_v2/deliveries/card/selectors'
import { addEnumValBySchema } from '../../../../shared/lib/addEnumValBySchema'
import { useAppDispatch } from '../../../../hooks/useAppDispatch/useAppDispatch'

interface ModalPaymentAmountProps {
  className?: string
  entityId?: string | number
  isOpen?: boolean
  onClose?: () => void
  date?: string
}

const { resultSchema, resultObject } = convertData2JsonForm(schema)

export const ModalShippingDate: React.FC<ModalPaymentAmountProps> = memo(
  (props: ModalPaymentAmountProps) => {
    const dispatch = useAppDispatch()
    const [disabledButtons, setDisabledButtons] = useState<boolean>(false)
    const { className, entityId, isOpen, onClose, date } = props

    if (!entityId) {
      return null
    }

    const form = useRef<any>({
      resultSchema,
      resultObject,
    })

    useEffect(() => {
      if (date) {
        form.current.resultObject.date = date
      }
    }, [date])

    const enums = useSelector(getDeliveriesEnumsData)

    if (enums?.enum?.transfer_reasons && resultSchema?.properties.hasOwnProperty('reason_id')) {
      const enumValues = enums?.['enum']?.['transfer_reasons'] || {}
      const oneOf: Record<string, string>[] = []

      Object.keys(enumValues).forEach(enumValue => {
        return oneOf.push({
          const: enumValues[enumValue].id,
          title: enumValues[enumValue].value,
        })
      })

      if (resultSchema?.properties.hasOwnProperty('reason_id')) {
        addEnumValBySchema(resultSchema, 'reason_id', oneOf, enumValues)
      }
    }

    const [isSuccess, setIsSuccess] = useAutoCloseModal(onClose)

    const changeFormData = useCallback(
      async ({ formData }) => {
        const isFilesEdit = JSON.stringify(formData) === JSON.stringify(form.current?.resultObject)
        if (isFilesEdit) {
          return
        }
        form.current.resultObject = { ...formData }
      },
      [form],
    )

    const handleAddPayment = useCallback(async () => {
      if (!form.current) {
        return
      }
      const controller = new AbortController()
      const data = { ...form.current?.resultObject }
      const isRequired = isFormDataRequired(data, form.current?.resultSchema)

      if (!isRequired) {
        Modal.warning({
          title: 'Предупреждение',
          content: 'Заполните все обязательные поля',
        })
        return
      }

      setDisabledButtons(true)
      await setShippingDate(entityId, data, controller.signal)
        .then(response => {
          if (response.error) {
            Modal.error({
              title: 'Ошибка',
              content: `При внесении оплаты возникла ошибка: ${response.error}. Внесение оплаты не сохранено.`,
            })
            return
          }
          setIsSuccess(true)
          dispatch(getDeliveriesForm(entityId))
        })
        .catch(err => {
          console.log({ err })
          Modal.error({
            title: 'Ошибка',
            content:
              err?.message || 'При внесении оплаты возникла ошибка. Внесение оплаты не сохранено.',
          })
          return
        })
        .finally(() => {
          setDisabledButtons(false)
        })
    }, [form, entityId, setIsSuccess, date])

    return (
      <Modal
        title="Изменение даты отгрузки"
        open={isOpen}
        onOk={handleAddPayment}
        onCancel={onClose}
        okButtonProps={{ disabled: disabledButtons }}
        cancelButtonProps={{ disabled: disabledButtons }}
        className={classNames('modal-payment-amount', {}, [className])}
      >
        {isSuccess && (
          <div style={{ marginBottom: 40 }}>
            <Alert message="Операция успешно выполнена:" showIcon type="success" />
          </div>
        )}
        {form.current ? (
          <DefaultJsonForm
            formData={{ ...form.current.resultObject }}
            schema={form.current.resultSchema}
            onChange={changeFormData}
            orientation="horizontal"
          />
        ) : (
          <Skeleton active />
        )}
      </Modal>
    )
  },
)
